import { useState, ElementType } from 'react';
import { Icon } from '@iconify/react';
import { NavLink as RouterLink, matchPath, useLocation } from 'react-router-dom';
import arrowIosForwardFill from '@iconify/icons-eva/arrow-ios-forward-fill';
import arrowIosDownwardFill from '@iconify/icons-eva/arrow-ios-downward-fill';
// material
import { alpha, useTheme, styled } from '@mui/material/styles';
import {
  Box,
  List,
  BoxProps,
  Collapse,
  ListItemText,
  ListItemIcon,
  ListSubheader,
  ListItemButton,
  ListItemButtonProps
} from '@mui/material';

// ----------------------------------------------------------------------

const ListSubheaderStyle = styled((props) => (
  <ListSubheader disableSticky disableGutters {...props} />
))(({ theme }) => ({
  ...theme.typography.overline,
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(2),
  paddingLeft: '20px',
  color: theme.palette.text.primary,
  textTransform: 'none',
  // color: '#9396BF',
  fontSize: '16px',
  lineHeight: '20px'
}));

interface ListItemStyleProps extends ListItemButtonProps {
  component?: ElementType;
  to?: string;
  href?: string;
  target?: string;
  rel?: string;
}

const ListItemStyle = styled(ListItemButton)<ListItemStyleProps>(({ theme }) => ({
  ...theme.typography.body2,
  height: 48,
  position: 'relative',
  // textTransform: 'capitalize',
  paddingLeft: theme.spacing(9),
  paddingRight: theme.spacing(2.5),
  // color: theme.palette.text.secondary,
  // color: '#383E80',
  color: '#333333',
  fontSize: 16,
  '&:before': {
    top: 0,
    right: 0,
    width: 3,
    bottom: 0,
    content: "''",
    display: 'none',
    position: 'absolute',
    borderTopLeftRadius: 4,
    borderBottomLeftRadius: 4,
    backgroundColor: theme.palette.primary.main
    // backgroundColor: '#383E80'
  }
}));

const ListItemIconStyle = styled(ListItemIcon)({
  width: 28,
  height: 28,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
});

// Indent for the 題目列表 subject sub-nav leaves (全部學科 / subjects / 更多).
// They carry no icon, so the empty icon slot is dropped and this pl seats the
// text under the parent title text instead of out at the icon column (the
// reported "sub-items surpass the title" mis-alignment).
const INSET_SUB_SX = {
  pl: 14,
  fontSize: 14,
  // Long subject names (e.g. "General Knowledge: Culture") wrap to multiple
  // lines; let the row grow instead of overflowing the base 48px height and
  // overlapping the next item.
  height: 'auto',
  minHeight: 44,
  py: 0.75,
  lineHeight: 1.35
} as const;

// ----------------------------------------------------------------------

type ChildNavItemProps = {
  title: string;
  path: string;
  icon?: JSX.Element;
  onClick?: () => void;
  notPermission?: boolean;
  // 3rd-level entries (subject filters or campaign links). Only one extra level is
  // supported. `onClick`/`isAction` make a 更多/收起 row that toggles the list
  // instead of navigating; `inset` aligns the icon-less subject rows under the
  // parent title. All optional — absent ⇒ other submenus render unchanged.
  children?: {
    title: string;
    path: string;
    notPermission?: boolean;
    onClick?: () => void;
    isAction?: boolean;
    inset?: boolean;
  }[];
};

type NavItemProps = {
  title: string;
  path: string;
  icon?: JSX.Element;
  info?: JSX.Element;
  children?: ChildNavItemProps[];
  notPermission?: boolean;
  src?: string;
};

function NavSubItem({
  item,
  activeSubStyle
}: {
  item: ChildNavItemProps;
  activeSubStyle: { color: string; fontWeight: string };
}) {
  const { pathname, search } = useLocation();
  const { title, path, icon, children } = item;
  const fullPath = `${pathname}${search}`;
  const isActiveSub = path
    ? !!matchPath({ path, end: true }, pathname) || fullPath === path
    : false;
  const isActiveBranch =
    (children?.length && path ? !!matchPath({ path, end: false }, pathname) : false) ||
    children?.some((grand) => fullPath === grand.path) ||
    isActiveSub;
  const [open, setOpen] = useState(isActiveBranch);

  if (!children?.length) {
    if (item.onClick) {
      return (
        <ListItemStyle
          onClick={item.onClick}
          aria-current={isActiveSub ? 'page' : undefined}
          sx={{
            ...(isActiveSub && activeSubStyle)
          }}
        >
          <ListItemIconStyle>{icon && icon}</ListItemIconStyle>
          <ListItemText disableTypography primary={title} />
        </ListItemStyle>
      );
    }
    return (
      <ListItemStyle
        component={RouterLink}
        to={path}
        aria-current={isActiveSub ? 'page' : undefined}
        sx={{
          ...(isActiveSub && activeSubStyle)
        }}
      >
        <ListItemIconStyle>{icon && icon}</ListItemIconStyle>
        <ListItemText disableTypography primary={title} />
      </ListItemStyle>
    );
  }

  return (
    <>
      <ListItemStyle
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        sx={{ ...(isActiveBranch && activeSubStyle) }}
      >
        <ListItemIconStyle>{icon && icon}</ListItemIconStyle>
        <ListItemText disableTypography primary={title} />
        <Box
          component={Icon}
          icon={open ? arrowIosDownwardFill : arrowIosForwardFill}
          sx={{ width: 14, height: 14, ml: 1 }}
        />
      </ListItemStyle>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <List component="div" disablePadding sx={{ pl: 2 }}>
          {children.map((grandchild) => {
            if (grandchild.notPermission) return null;
            // Subject rows (題目列表) carry `inset` → align under the parent
            // title; other submenus keep the original pl:6.
            const baseSx = grandchild.inset ? INSET_SUB_SX : { pl: 6, fontSize: 14 };
            // 更多 / 收起 action row — toggles the subject list, never navigates.
            if (grandchild.isAction || grandchild.onClick) {
              return (
                <ListItemStyle
                  key={grandchild.title}
                  onClick={grandchild.onClick}
                  // 更多/收起 is an action, not a subject — paint it the project
                  // accent (green) so it reads as a toggle, not a black label.
                  sx={{ ...baseSx, color: 'primary.main', fontWeight: 600 }}
                >
                  <ListItemText disableTypography primary={grandchild.title} />
                </ListItemStyle>
              );
            }
            const isActiveGrand = fullPath === grandchild.path;
            return (
              <ListItemStyle
                key={grandchild.title}
                component={RouterLink}
                to={grandchild.path}
                aria-current={isActiveGrand ? 'page' : undefined}
                sx={{
                  ...baseSx,
                  ...(isActiveGrand && activeSubStyle)
                }}
              >
                <ListItemText disableTypography primary={grandchild.title} />
              </ListItemStyle>
            );
          })}
        </List>
      </Collapse>
    </>
  );
}

function NavItem({ item, isShow }: { item: NavItemProps; isShow?: boolean | undefined }) {
  const theme = useTheme();
  const { pathname } = useLocation();
  const { title, path, icon, info, children } = item;
  const isActiveRoot =
    (path
      ? !!matchPath({ path, end: false }, pathname) ||
        (path === '/dashboard/parentChildTask/list' &&
          pathname.startsWith('/dashboard/parentChildTask/'))
      : false) ||
    children?.some(
      (child) => child.path && !!matchPath({ path: child.path, end: false }, pathname)
    );

  const [open, setOpen] = useState(isActiveRoot);

  const handleOpen = () => {
    setOpen(!open);
  };

  const activeRootStyle = {
    color: 'primary.main',
    // color: '#383E80',
    fontWeight: 'fontWeightMedium',
    bgcolor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
    // bgcolor: '#E4EDFF',
    '&:before': { display: 'block' }
  };

  const activeSubStyle = {
    color: 'primary.main',
    fontWeight: 'fontWeightMedium'
  };

  if (children) {
    return (
      <>
        <ListItemStyle
          onClick={handleOpen}
          aria-expanded={open}
          sx={{
            paddingLeft: isShow ? '40px' : '20px',
            ...(isActiveRoot && activeRootStyle),
            ...(isActiveRoot && {
              color: 'inherit',
              fontWeight: 'inherit',
              borderColor: 'transparent',
              bgcolor: 'transparent',
              '&:before': { display: 'none' }
            })
          }}
        >
          <ListItemIconStyle>{icon && icon}</ListItemIconStyle>

          {isShow && (
            <>
              <ListItemText disableTypography primary={title} />
              {info && info}
              <Box
                component={Icon}
                icon={open ? arrowIosDownwardFill : arrowIosForwardFill}
                sx={{ width: 16, height: 16, ml: 1 }}
              />
            </>
          )}
        </ListItemStyle>

        {isShow && (
          <Collapse in={open} timeout="auto" unmountOnExit>
            <List
              component="div"
              disablePadding
              sx={{
                mt: 0.5,
                mb: 1.5,
                ml: 0,
                px: 0.75,
                py: 0.5,
                borderRadius: 2,
                bgcolor: 'transparent'
              }}
            >
              {children.map((child) => {
                if (child.notPermission) return null;
                return (
                  <NavSubItem key={child.title} item={child} activeSubStyle={activeSubStyle} />
                );
              })}
            </List>
          </Collapse>
        )}
      </>
    );
  }

  if (item?.src) {
    return (
      <ListItemStyle
        sx={{ paddingLeft: isShow ? '40px' : '20px' }}
        component="a"
        href={item.src}
        target="_blank"
        rel="noopener noreferrer"
      >
        <ListItemIconStyle>{icon && icon}</ListItemIconStyle>
        {isShow && (
          <>
            <ListItemText disableTypography primary={title} />
            {info && info}
          </>
        )}
      </ListItemStyle>
    );
  }
  return (
    <ListItemStyle
      component={RouterLink}
      to={path}
      sx={{
        ...(isActiveRoot && activeRootStyle),
        paddingLeft: isShow ? '40px' : '20px'
      }}
    >
      <ListItemIconStyle>{icon && icon}</ListItemIconStyle>
      {isShow && (
        <>
          <ListItemText disableTypography primary={title} />
          {info && info}
        </>
      )}
    </ListItemStyle>
  );
}

interface NavSectionProps extends BoxProps {
  isShow?: boolean | undefined;
  navConfig: any[];
}

export default function NavSection({ navConfig, isShow = true, ...other }: NavSectionProps) {
  return (
    <Box {...other}>
      {navConfig.map((list) => {
        const { subheader, items, notPermission } = list;

        if (notPermission) return <></>;

        return (
          <List key={subheader} disablePadding>
            {isShow && <ListSubheaderStyle>{subheader}</ListSubheaderStyle>}
            {items.map((item: NavItemProps) => {
              if (item.notPermission) return <></>;

              return <NavItem key={item.title} item={item} isShow={isShow} />;
            })}
          </List>
        );
      })}
    </Box>
  );
}
